/**
 * @file appointment.js
 * Handles the logic for the appointment date and time selection.
 */

import { updateValidDays, updateValidMonths } from "./utils.js";

export const APPOINTMENT_CONFIG = {
  // timeZone: "America/Chicago",
  timeZone: "America/Los_Angeles",
  // timeZone: "Asia/Bangkok",
  availability: {
    // Sunday: { start: { hour: 9, minute: 0 }, end: { hour: 21, minute: 0 } },
    // Tuesday: { start: { hour: 9, minute: 15 }, end: { hour: 21, minute: 0 } },
    // Saturday: { start: null, end: null },
    // Saturday: { start: { hour: null, minute: null }, end: { hour: null, minute: null } },
    Sunday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Monday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Tuesday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Wednesday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Thursday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Friday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
    Saturday: { start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } },
  },
  interval: 15,
};

const daySelect = document.getElementById("day-select");
const monthSelect = document.getElementById("month-select");
const yearSelect = document.getElementById("year-select");
const timepicker = document.getElementById("timepicker");

/**
 * Populates the appointment day and year dropdowns.
 */
function populateAppointmentDropdowns() {
  const currentYear = new Date().getFullYear();
  const currentBuddhistYear = currentYear + 543;

  for (let day = 1; day <= 31; day++) {
    const option = document.createElement("option");
    option.value = day;
    option.textContent = day;
    daySelect.appendChild(option);
  }

  for (let i = 0; i <= 1; i++) {
    const buddhistYear = currentBuddhistYear + i;
    const gregorianYear = buddhistYear - 543;
    const option = document.createElement("option");
    option.value = gregorianYear;
    option.textContent = buddhistYear;
    if (gregorianYear === currentYear) {
      option.selected = true;
    }
    yearSelect.appendChild(option);
  }
}

/**
 * Validation rule for appointment dates. Returns true if the date is in the past.
 */
function isInvalidAppointmentDate(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Checks if current time is past the last available appointment slot
 */
function isPastLastAppointmentSlot() {
  const now = new Date();
  const dayOfWeek = now.toLocaleDateString("en-US", { weekday: "long" });
  const todayAvailability = APPOINTMENT_CONFIG.availability[dayOfWeek];

  if (
    !todayAvailability ||
    !todayAvailability.end ||
    todayAvailability.end.hour === null
  ) {
    return true; // No availability defined for today
  }

  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  // Calculate the last appointment slot in local time
  const lastTexasHour = todayAvailability.end.hour;
  const lastTexasMinute =
    todayAvailability.end.minute - APPOINTMENT_CONFIG.interval;

  const lastTexasDT = luxon.DateTime.fromObject(
    {
      hour: lastTexasHour,
      minute: lastTexasMinute,
    },
    { zone: APPOINTMENT_CONFIG.timeZone }
  );

  const lastLocalDT = lastTexasDT.setZone(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const lastSlotTimeInMinutes = lastLocalDT.hour * 60 + lastLocalDT.minute;

  return currentTimeInMinutes > lastSlotTimeInMinutes;
}

/**
 * Gets the appropriate default date (today or tomorrow based on current time)
 */
function getDefaultDate() {
  const today = new Date();

  if (isPastLastAppointmentSlot()) {
    // If past last appointment slot, default to tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  }

  return today;
}

/**
 * Updates the validity of time slots by rebuilding the dropdown.
 * This version removes/re-adds options to support iOS.
 */
function updateValidTimeSlots() {
  const day = parseInt(daySelect.value);
  const month = parseInt(monthSelect.value);
  const year = parseInt(yearSelect.value);

  const originalValue = timepicker.value;
  const placeholder = timepicker.querySelector('option[value=""]');

  timepicker.disabled = false;

  if (placeholder) {
    placeholder.textContent = "เวลา"; // Reset placeholder text
  }

  timepicker.innerHTML = "";
  if (placeholder) {
    timepicker.appendChild(placeholder);
  }

  if (!day || !month || !year) {
    return;
  }

  const selectedDate = new Date(year, month - 1, day);
  const dayOfWeek = selectedDate.toLocaleDateString("en-US", {
    weekday: "long",
  });
  const availability = APPOINTMENT_CONFIG.availability[dayOfWeek];

  let hasAvailableSlots = false;

  if (
    availability &&
    availability.start &&
    availability.end &&
    availability.start.hour !== null &&
    availability.end.hour !== null
  ) {
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();
    const currentTimeInMinutes = today.getHours() * 60 + today.getMinutes();
    const blockDurationInMinutes = 8 * 60; // 8 hours

    for (
      let hour = availability.start.hour;
      hour <= availability.end.hour;
      hour++
    ) {
      const startMin =
        hour === availability.start.hour ? availability.start.minute : 0;
      const endMin =
        hour === availability.end.hour ? availability.end.minute : 60;

      for (let min = startMin; min < endMin; min += APPOINTMENT_CONFIG.interval) {
        const texasDT = luxon.DateTime.fromObject(
          { hour, minute: min },
          { zone: APPOINTMENT_CONFIG.timeZone }
        );
        const localDT = texasDT.setZone(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        const optionTimeInMinutes = localDT.hour * 60 + localDT.minute;

        if (
          !isToday ||
          (isToday &&
            optionTimeInMinutes >
              currentTimeInMinutes + blockDurationInMinutes)
        ) {
          const option = document.createElement("option");
          option.value = localDT.toFormat("HH:mm");
          option.textContent = localDT.toFormat("HH:mm");
          option.dataset.texas = texasDT.toFormat("HH:mm");
          timepicker.appendChild(option);
          hasAvailableSlots = true;
        }
      }
    }
  }

  if (!hasAvailableSlots) {
    if (placeholder) {
      placeholder.textContent = "คิวเต็มแล้ว กรุณาเลือกวันต่อไป";
    }
    timepicker.disabled = true;
  }

  timepicker.value = originalValue;
  if (timepicker.selectedIndex === -1) {
    timepicker.value = "";
  }
}

/**
 * Updates the hidden date field with the ISO date string.
 */
function updateHiddenDate() {
  const hiddenDate = document.getElementById("hidden-date");
  const day = daySelect.value;
  const month = monthSelect.value;
  const year = yearSelect.value;

  hiddenDate.value =
    day && month && year
      ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
          2,
          "0"
        )}`
      : "";
}

function handleDateChange() {
  const year = parseInt(yearSelect.value);
  const month = parseInt(monthSelect.value);

  if (year) updateValidMonths(monthSelect, year, isInvalidAppointmentDate);
  if (year && month)
    updateValidDays(daySelect, month, year, isInvalidAppointmentDate);

  updateValidTimeSlots();
  updateHiddenDate();
}

/**
 * Initializes the appointment form.
 */
export function initAppointmentForm() {
  if (!daySelect || !monthSelect || !yearSelect || !timepicker) return;

  populateAppointmentDropdowns();

  // Use the smart default date (today or tomorrow based on current time)
  const defaultDate = getDefaultDate();
  monthSelect.value = defaultDate.getMonth() + 1;
  daySelect.value = defaultDate.getDate();

  // If using tomorrow and it's in a different year, update the year
  if (defaultDate.getFullYear() !== new Date().getFullYear()) {
    yearSelect.value = defaultDate.getFullYear();
  }

  handleDateChange(); // This will now correctly populate the time slots on initial load

  [yearSelect, monthSelect, daySelect].forEach((el) =>
    el.addEventListener("change", handleDateChange)
  );

  timepicker.addEventListener("change", () => {
    const selected = timepicker.selectedOptions[0];
    const texasDatetimeEl = document.getElementById("texas_datetime");
    if (texasDatetimeEl && selected) {
      texasDatetimeEl.value = selected.dataset.texas;
    }
  });
}
